import { Button, Tabs } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'
import { ContentConfirmDialogView } from './content-confirm-dialog.view'
import type { LanguageTabView, Translate, TranslationOriginView } from './content-types'
import { DraftEditorView } from './draft-editor.view'
import { MarkdownPreviewView } from './markdown-preview.view'

/**
 * Props for {@link LanguageTabsView}.
 *
 * @property selected - The locale whose tab is shown.
 * @property editable - The selected localization is a draft the viewer may edit.
 * @property draft - Title/body as edited (or stored, when read-only).
 * @property lifecycle - The selected tab's lifecycle bar (a {@link LifecycleBarView}).
 * @property onAddLanguage - Present only with `content.write`.
 * @property discardOpen - The unsaved-edits confirmation is open.
 */
export interface LanguageTabsViewProps {
	t: Translate
	tabs: LanguageTabView[]
	selected: string
	origin: TranslationOriginView | null
	sourceChanged: boolean
	editable: boolean
	draft: { title: string; body: string }
	errors: Record<string, string>
	lifecycle: ReactNode
	discardOpen: boolean
	onSelect: (locale: string) => void
	onChange: (field: 'title' | 'body', value: string) => void
	onAddLanguage?: () => void
	onStay: () => void
	onDiscard: () => void
}

function Panel({ props }: { props: LanguageTabsViewProps }): ReactElement {
	const { t } = props
	return (
		<div className="flex flex-col gap-3">
			{props.origin ? (
				<p className="text-ds-sm text-ds-text-muted">{t('editor.origin', { origin: t(`origin.${props.origin}`) })}</p>
			) : null}
			{props.sourceChanged ? (
				<p role="status" className="text-ds-sm text-ds-danger">
					{t('editor.sourceChanged')}
				</p>
			) : null}
			{props.editable ? (
				<DraftEditorView
					t={t}
					title={props.draft.title}
					body={props.draft.body}
					errors={props.errors}
					onChange={props.onChange}
				/>
			) : (
				<article className="flex flex-col gap-2">
					<h3 className="text-ds-lg font-semibold text-ds-text">{props.draft.title}</h3>
					<MarkdownPreviewView label={t('editor.preview')} markdown={props.draft.body} />
				</article>
			)}
			{props.lifecycle}
		</div>
	)
}

/** One tab per language of the selected version, with the draft editor or the read-only body (organism). */
export function LanguageTabsView(props: LanguageTabsViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3" aria-label={t('editor.languages')}>
			<div className="flex items-center justify-between">
				<h2 className="text-ds-lg font-semibold text-ds-text">{t('editor.languages')}</h2>
				{props.onAddLanguage ? (
					<Button variant="muted" size="sm" onPress={props.onAddLanguage}>
						{t('editor.addLanguage')}
					</Button>
				) : null}
			</div>
			{props.tabs.length ? (
				<Tabs
					aria-label={t('editor.languages')}
					selectedKey={props.selected}
					onSelectionChange={(key) => props.onSelect(String(key))}
					tabs={props.tabs.map((tab) => ({
						id: tab.locale,
						label: `${tab.locale} · ${t(`status.${tab.label}`)}`,
						content: tab.locale === props.selected ? <Panel props={props} /> : null,
					}))}
				/>
			) : null}
			<ContentConfirmDialogView
				t={t}
				isOpen={props.discardOpen}
				title={t('editor.discardTitle')}
				body={t('editor.discardBody')}
				confirmLabel={t('editor.discard')}
				cancelLabel={t('editor.stay')}
				danger
				onConfirm={props.onDiscard}
				onCancel={props.onStay}
			/>
		</section>
	)
}
